// ─────────────────────────────────────────────────────────────────────────
// sequencer - the client-side "suggested order" engine (boardroom Option A).
//
// Pure, deterministic, offline, zero-API. Given the day's stops (each a
// briefed place with coords), it returns a SUGGESTED visiting order:
//   nearest-neighbor seed -> 2-opt improvement over haversine distance.
//
// It is NOT a drive-time optimizer. It knows nothing about one-ways, rivers,
// or traffic, so its output is labeled "suggested order", never "optimized"
// (see industryLexicon.routeLabels). Traffic-aware routing is handed to
// Google Maps at export time (mapsExport.js) or the paid Routes path later.
//
// HARD PINS (boardroom, non-negotiable): a stop with a booked appointment
// window is a FIXED position in the day. Free stops are ordered only in the
// gaps between pins. A pin is NEVER reordered and NEVER dropped. Missing a
// booked 10am is the one unforgivable error, so pin handling is the first
// thing the tests assert.
//
// Runs in a Web Worker in production (stop-count budget + iteration
// time-box) so a 40-stop morning cannot jank the Today hero. The budget and
// time-box are parameters here; the Worker wrapper lives in the bundle.
// ─────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} Stop
 * @property {string} id
 * @property {number} lat
 * @property {number} lng
 * @property {string} [name]
 * @property {number} [apptStart] - epoch ms of a booked window start; presence => HARD PIN
 * @property {number} [apptEnd]   - epoch ms of the window end (optional)
 * @property {number} [priority]  - higher = more important; tie-breaker only, never overrides a pin
 */

/**
 * @typedef {Object} SequenceResult
 * @property {Stop[]} order        - stops in suggested visiting order (pins fixed by apptStart)
 * @property {Stop[]} unplaceable  - stops with missing/invalid coords, surfaced for manual fix (never silently dropped)
 * @property {number} pins         - count of hard-pinned stops honored
 * @property {boolean} timedOut    - true if the 2-opt loop hit the time-box before converging
 */

const EARTH_RADIUS_KM = 6371;

/** Finite, in-range WGS84 coordinate check. */
export function hasValidCoords(s) {
  return (
    s != null &&
    Number.isFinite(s.lat) &&
    Number.isFinite(s.lng) &&
    s.lat >= -90 && s.lat <= 90 &&
    s.lng >= -180 && s.lng <= 180
  );
}

/** Great-circle distance in km between two coord-bearing points. */
export function haversineKm(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** A stop is a hard pin iff it carries a finite booked-window start. */
export function isPinned(s) {
  return s != null && Number.isFinite(s.apptStart);
}

/** Total great-circle length of an ordered list (0 for <2 stops). */
export function pathLengthKm(order) {
  let total = 0;
  for (let i = 1; i < order.length; i++) total += haversineKm(order[i - 1], order[i]);
  return total;
}

/**
 * Nearest-neighbor ordering of a set of FREE stops, starting from `from`
 * (or the first stop when `from` is null). Pure; does not mutate input.
 *
 * `discountOf` (optional) returns a km-scale OPPORTUNITY bias for a candidate:
 * the greedy score is `haversineKm(cursor, candidate) - discountOf(candidate)`,
 * so a higher-opportunity stop is favored when two candidates are otherwise a
 * near-equal distance away. Distance stays primary (the bias is capped small by
 * the caller); opportunity only breaks near-ties. Null => pure distance.
 */
function nearestNeighbor(stops, from, discountOf) {
  const remaining = stops.slice();
  const out = [];
  let cursor = from;
  while (remaining.length) {
    let bestIdx = 0;
    if (cursor) {
      let bestScore = Infinity;
      for (let i = 0; i < remaining.length; i++) {
        const disc = discountOf ? discountOf(remaining[i]) : 0;
        const score = haversineKm(cursor, remaining[i]) - disc;
        if (score < bestScore) { bestScore = score; bestIdx = i; }
      }
    }
    cursor = remaining.splice(bestIdx, 1)[0];
    out.push(cursor);
  }
  return out;
}

/**
 * 2-opt improvement over a free segment with fixed endpoints `before` and
 * `after` (either may be null at the ends of the day). Time-boxed: returns
 * as soon as `deadline` (epoch ms, from an injected clock) passes.
 *
 * `discountOf` (optional) folds a SMALL positional opportunity penalty into the
 * segment cost: a high-opportunity stop placed late in the segment (large index
 * fraction) costs a little extra, so 2-opt keeps strong stops early when the
 * pure-distance saving is marginal. It is bounded by the caller's km-scale bias
 * and never overrides a real distance improvement. Null => pure distance (the
 * exact legacy behavior, so distance-only callers/tests are unaffected).
 */
function twoOpt(segment, before, after, deadline, now, discountOf) {
  if (segment.length < 3) return { order: segment, timedOut: false };
  let best = segment.slice();
  const segLen = (arr) => {
    let t = 0;
    let prev = before;
    for (let i = 0; i < arr.length; i++) {
      if (prev) t += haversineKm(prev, arr[i]);
      if (discountOf) t += (i / arr.length) * discountOf(arr[i]);
      prev = arr[i];
    }
    if (after) t += haversineKm(prev, after);
    return t;
  };
  let bestLen = segLen(best);
  let improved = true;
  let timedOut = false;
  while (improved) {
    improved = false;
    for (let i = 0; i < best.length - 1; i++) {
      for (let k = i + 1; k < best.length; k++) {
        if (now() > deadline) { timedOut = true; improved = false; break; }
        const candidate = best.slice(0, i)
          .concat(best.slice(i, k + 1).reverse(), best.slice(k + 1));
        const cLen = segLen(candidate);
        if (cLen + 1e-9 < bestLen) { best = candidate; bestLen = cLen; improved = true; }
      }
      if (timedOut) break;
    }
  }
  return { order: best, timedOut };
}

/**
 * Sequence the day. Pins (stops with apptStart) hold their chronological
 * positions as fixed anchors; free stops are ordered by proximity in the
 * gaps between consecutive pins. Stops with invalid coords are returned in
 * `unplaceable` (never dropped from the day).
 *
 * @param {Stop[]} stops
 * @param {Object} [opts]
 * @param {number} [opts.maxStops=60]   - stop-count budget; beyond this, 2-opt is skipped (NN only) to protect the frame
 * @param {number} [opts.timeBudgetMs=120] - wall-clock ceiling for all 2-opt work combined
 * @param {() => number} [opts.now]     - injectable clock (epoch ms) for deterministic tests
 * @param {{lat:number,lng:number}} [opts.start] - fixed START anchor (a typed/geocoded origin, NOT a stop). Free stops are ordered outward from here.
 * @param {{lat:number,lng:number}} [opts.end]   - fixed END anchor (a typed/geocoded destination, NOT a stop). The order closes toward here.
 * @param {number} [opts.opportunityBiasKm=0] - km-scale ceiling for the opportunity bias. Each stop's `oppNorm` (0..1) buys at most this many km of "pull" earlier. Distance stays primary; 0 disables the bias entirely (legacy behavior).
 * @returns {SequenceResult}
 */
export function sequenceDay(stops, opts = {}) {
  const maxStops = opts.maxStops != null ? opts.maxStops : 60;
  const timeBudgetMs = opts.timeBudgetMs != null ? opts.timeBudgetMs : 120;
  const now = typeof opts.now === 'function'
    ? opts.now
    : (typeof performance !== 'undefined' && performance.now ? () => performance.now() : () => 0);

  // Fixed START/END anchors (origin/destination the rep typed). They are NOT
  // stops: they never appear in the returned order, they only bias where the
  // real stops fall (ordered outward from start, closing toward end).
  const start = hasValidCoords(opts.start) ? opts.start : null;
  const end = hasValidCoords(opts.end) ? opts.end : null;

  // OPPORTUNITY WEIGHTING (documented, board honesty contract):
  //   distance is ALWAYS primary. Opportunity is a small tie-breaker bias,
  //   expressed in km via opportunityBiasKm, applied to each stop's normalized
  //   `oppNorm` in [0,1] (computed upstream from signalScores, e.g.
  //   (spend+timing+openness)/30). A max-opportunity stop gets at most
  //   opportunityBiasKm km of pull earlier; it can never override a genuine
  //   distance saving larger than that ceiling. When the ceiling is 0 (default)
  //   discountOf is null and the solver is byte-for-byte the legacy distance-only
  //   engine, so existing callers and tests are unaffected.
  const oppBiasKm = Number.isFinite(opts.opportunityBiasKm) ? opts.opportunityBiasKm : 0;
  const discountOf = oppBiasKm > 0
    ? (s) => {
        const n = s && Number.isFinite(s.oppNorm) ? s.oppNorm : 0;
        return oppBiasKm * Math.max(0, Math.min(1, n));
      }
    : null;

  const list = Array.isArray(stops) ? stops : [];
  const placeable = list.filter(hasValidCoords);
  const unplaceable = list.filter((s) => !hasValidCoords(s));

  // Pins first, in chronological order of their booked windows. Stable: if
  // two share a start, input order breaks the tie (deterministic).
  const pins = placeable
    .filter(isPinned)
    .map((s, i) => ({ s, i }))
    .sort((a, b) => (a.s.apptStart - b.s.apptStart) || (a.i - b.i))
    .map((x) => x.s);
  const free = placeable.filter((s) => !isPinned(s));

  // No pins: order the whole day by proximity (NN seed + one 2-opt pass).
  const deadline = now() + timeBudgetMs;
  let timedOut = false;

  if (pins.length === 0) {
    if (free.length <= 2) {
      // With a start anchor and two free stops, order them nearest-first from
      // the origin; otherwise nothing to sequence (input order preserved).
      const ord = (start && free.length === 2) ? nearestNeighbor(free, start, discountOf) : free.slice();
      return { order: ord, unplaceable, pins: 0, timedOut: false };
    }
    const seeded = nearestNeighbor(free, start || free[0], discountOf);
    if (free.length > maxStops) return { order: seeded, unplaceable, pins: 0, timedOut: false };
    const r = twoOpt(seeded, start, end, deadline, now, discountOf);
    return { order: r.order, unplaceable, pins: 0, timedOut: r.timedOut };
  }

  // With pins: fill each gap [prevPin .. nextPin] with the free stops that
  // are nearest to that segment's anchors. Assign each free stop to the gap
  // whose nearest anchor is closest, then NN+2-opt within the gap.
  const anchors = pins; // fixed points in chronological order
  const gaps = new Array(anchors.length + 1); // gap g sits BEFORE anchor g (last is after the final anchor)
  for (let g = 0; g <= anchors.length; g++) gaps[g] = [];

  // Gap boundaries. The first gap opens at the START anchor (when provided),
  // the last gap closes at the END anchor; interior gaps sit between two pins.
  const anchorFor = (g) => (g < anchors.length ? anchors[g] : end);
  const anchorBefore = (g) => (g > 0 ? anchors[g - 1] : start);

  for (const f of free) {
    let bestGap = 0;
    let bestD = Infinity;
    for (let g = 0; g <= anchors.length; g++) {
      const a = anchorBefore(g);
      const b = anchorFor(g);
      let d;
      if (a && b) d = Math.min(haversineKm(a, f), haversineKm(b, f));
      else if (a) d = haversineKm(a, f);
      else if (b) d = haversineKm(b, f);
      else d = 0;
      if (d < bestD) { bestD = d; bestGap = g; }
    }
    gaps[bestGap].push(f);
  }

  const order = [];
  for (let g = 0; g <= anchors.length; g++) {
    const before = anchorBefore(g);
    const after = anchorFor(g);
    let seg = gaps[g];
    if (seg.length > 1) {
      seg = nearestNeighbor(seg, before || seg[0], discountOf);
      if (seg.length >= 3 && placeable.length <= maxStops) {
        const r = twoOpt(seg, before, after, deadline, now, discountOf);
        seg = r.order;
        timedOut = timedOut || r.timedOut;
      }
    }
    order.push(...seg);
    if (g < anchors.length) order.push(anchors[g]); // the pin itself, fixed (never the end anchor)
  }

  return { order, unplaceable, pins: anchors.length, timedOut };
}
